import { inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { AnalyticsModuleName } from '../GA4/models/analytics-functionallity.models';

import { LoadAnalyticsModule } from './analytics.actions';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { LoadModule } from '@core/infrastructure/state/core-commands.actions';
import { getIsModuleLoaded } from '@core/infrastructure/state/core.selectors';
import { SidebarActionTypes } from '@features/sidebar';

/**
 * Para evitar que se cargue código de analíca en el servidor (SSR) se cargan los módulos de analítica
 * de forma lazy y con la comprobación previa de que se está en un entorno de navegador.
 * En el caso de los módulos que contienen los efectos con los puntos de entrada de los eventos de analítica
 * para la mayoría de funcionalidades individuales se cargan utilizando el efecto LoadAnalyticsModule$.
 * En el módulo de funcionalidad donde sea necesario se dispara la acción LoadAnalyticsModule con el
 * nombre del módulo de analítica requerido.
 */

const AnalyticsModules: Record<AnalyticsModuleName, any> = {
    // eslint-disable-next-line no-secrets/no-secrets
    // AnalyticsShopcartModule // Se carga junton con AnalyticsModule
    AnalyticsAuthenticationModule: () => import('@shared/analytics/features-analytics-modules/analytics-authentication.module').then((m) => m.AnalyticsAuthenticationModule),
    AnalyticsCategoryModule: () => import('@shared/analytics/features-analytics-modules/analytics-category.module').then((m) => m.AnalyticsCategoryModule),
    AnalyticsCheckoutModule: () => import('@shared/analytics/features-analytics-modules/analytics-checkout.module').then((m) => m.AnalyticsCheckoutModule),
    AnalyticsFeelSubscriptionModule: () => import('@shared/analytics/features-analytics-modules/analytics-feel-subscription.module').then((m) => m.AnalyticsFeelSubscriptionModule),
    AnalyticsGiftCardModule: () => import('@shared/analytics/features-analytics-modules/analytics-gift-card.module').then((m) => m.AnalyticsGiftCardModule),
    AnalyticsLinkedAccountModule: () => import('@shared/analytics/features-analytics-modules/analytics-linked-account.module').then((m) => m.AnalyticsLinkedAccountModule),
    AnalyticsMyAccountFeelModule: () => import('@shared/analytics/features-analytics-modules/analytics-my-account-feel.module').then((m) => m.AnalyticsMyAccountFeelModule),
    AnalyticsMyAccountModule: () => import('@shared/analytics/features-analytics-modules/analytics-my-account.module').then((m) => m.AnalyticsMyAccountModule),
    AnalyticsNewsletterModule: () => import('@shared/analytics/features-analytics-modules/analytics-newsletter.module').then((m) => m.AnalyticsNewsletterModule),
    // eslint-disable-next-line no-secrets/no-secrets
    // AnalyticsOrderConfirmationModule: () => import('@shared/analytics/features-analytics-modules/analytics-order-confirmation.module').then((m) => m.AnalyticsOrderConfirmationModule), // No se realiza la carga con LoadAnalyticsModule porque reacciona a un page_view que forma parte del core de analítica y no se cargaría a tiempo
    AnalyticsProductDetailModule: () => import('@shared/analytics/features-analytics-modules/analytics-product-detail.module').then((m) => m.AnalyticsProductDetailModule),
    AnalyticsRecoverPasswordModule: () => import('@shared/analytics/features-analytics-modules/analytics-recover-password.module').then((m) => m.AnalyticsRecoverPasswordModule),
    AnalyticsRegisterModule: () => import('@shared/analytics/features-analytics-modules/analytics-register.module').then((m) => m.AnalyticsRegisterModule),
    AnalyticsRelatedProductsModule: () => import('@shared/analytics/features-analytics-modules/analytics-related-products.module').then((m) => m.AnalyticsRelatedProductsModule),
    AnalyticsRunwayModule: () => import('@shared/analytics/features-analytics-modules/analytics-runway.module').then((m) => m.AnalyticsRunwayModule),
    AnalyticsSearchModule: () => import('@shared/analytics/features-analytics-modules/analytics-search.modulE').then((m) => m.AnalyticsSearchModule),
    AnalyticsSidebarModule: () => import('@shared/analytics/features-analytics-modules/analytics-sidebar.module').then((m) => m.AnalyticsSidebarModule),
    AnalyticsWorldwideModule: () => import('@shared/analytics/features-analytics-modules/analytics-worldwide.module').then((m) => m.AnalyticsWorldwideModule),
    AnalyticsBannerSystemModule: () => import('@shared/analytics/features-analytics-modules/analytics-banner-system.module').then((m) => m.AnalyticsBannerSystemModule),
};

const LoadAnalyticsModule$ = createEffect((
    actions$ = inject(Actions),
    platformService = inject(CorePlatformService),
) => actions$.pipe(
    ofType(LoadAnalyticsModule),
    filter(() => platformService.isBrowser()),
    map(({ moduleName }) => {
        const modulePath = AnalyticsModules[moduleName];
        return LoadModule({ module: moduleName, path: modulePath });
    }),
), { functional: true });

const LoadAnalyticsSidebarModule$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => actions$.pipe(
    ofType(SidebarActionTypes.OpenSidebar, SidebarActionTypes.OpenSidebarCustom),
    // eslint-disable-next-line no-secrets/no-secrets
    map(() => store.selectSignal(getIsModuleLoaded('AnalyticsSidebarModule'))()),
    filter((analyticsSidebarLoaded) => !analyticsSidebarLoaded),
    // eslint-disable-next-line no-secrets/no-secrets
    map(() => LoadAnalyticsModule({ moduleName: 'AnalyticsSidebarModule' })),
), { functional: true });

export const AnalyticsEffectsFunctional = {
    LoadAnalyticsModule$,
    LoadAnalyticsSidebarModule$,
};
